import React, { useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Heart, Send, Loader2, ArrowLeft, Check, ShieldCheck } from "lucide-react";
import { getLang } from "../i18n";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Mirrors the server-side PATH_CONFIG (server remains authoritative).
// Keys are the canonical English identifiers sent to the backend.
export const PATHS = {
  "Private Foundational": { min: 35, max: 85, recurring: false },
  "Shared Sanctuary": { min: 20, max: 45, recurring: true },
  "14-Day Shared Coherence Immersion": { min: 79, max: 180, recurring: false },
};
const PATH_NAMES = Object.keys(PATHS);

// Display labels only — the underlying value sent to the server stays English.
const PATH_LABELS = {
  en: {
    "Private Foundational": "Private Foundational",
    "Shared Sanctuary": "Shared Sanctuary",
    "14-Day Shared Coherence Immersion": "14-Day Shared Coherence Immersion",
  },
  de: {
    "Private Foundational": "Private Grundlegung",
    "Shared Sanctuary": "Geteilte Zuflucht",
    "14-Day Shared Coherence Immersion": "14-tägige Geteilte Kohärenz-Immersion",
  },
};

// Free Sunday Common Session — a message-only registration that never triggers payment.
export const SUNDAY_PATH = "Sunday Common Session — Free";
const SUNDAY_LABELS = {
  en: "Sunday Common Session — Free",
  de: "Sonntags-Gemeinschaftssitzung — Kostenlos",
};

const T = {
  en: {
    // request step
    reqTitle: "Share Your Request",
    reqSub: "Every field is held in confidence and warmth.",
    nameLabel: "Your name *",
    namePlaceholder: "Your full name",
    emailLabel: "Your email *",
    messageLabel: "Message / Intention *",
    messagePlaceholder: "Share whatever you are holding right now — as briefly or as fully as feels comfortable.",
    pathLabel: "Path *",
    pathPlaceholder: "Choose a Path",
    messageOnly: "Send message only",
    continueDonation: "Continue to donation",
    // validation
    needName: "Please share your name.",
    needEmail: "Please enter a valid email address.",
    needMessage: "Please share a few words about your intention.",
    needPath: "Please select a Path.",
    invalidAmount: "Please enter a valid amount.",
    minMsg: (p, m, unit) => `The minimum for ${p} is €${m}${unit}.`,
    perMonth: " per month",
    // sent step
    sentTitle: "Your request is received",
    sentBody: (name, id) => (
      <>Thank you, {name}. Your intention rests with us and we will respond personally. Your reference is <strong style={{ color: "var(--ink)" }}>{id}</strong>.</>
    ),
    sendAnother: "Send another request",
    okMessage: "Your message has been received. We will reach out with care.",
    errSend: "Something interrupted the send. Please try again.",
    // sunday common session (free)
    sundayNote: "Every Sunday · 09:00 AM (CEST) · Approximately 20 minutes · Free",
    sundayRegister: "Register for the Sunday Session",
    sundaySentTitle: "You are registered",
    sundaySentBody: "Thank you. You are registered for the Sunday Common Session. You will receive the details by email.",
    sundayDefaultMsg: "I would like to join the free Sunday Common Session.",
    // donation step
    back: "Back to request",
    danaTitle: "Offer Your Dana",
    danaSub: (p, recurring, min, max) => (
      <>For <strong style={{ color: "var(--ink)" }}>{p}</strong> — a {recurring ? "monthly contribution" : "one-time contribution"} in Euro. Suggested range €{min}–€{max}{recurring ? " / month" : ""}.</>
    ),
    monthlyLabel: "Monthly contribution (EUR)",
    onceLabel: "Contribution (EUR)",
    minPlaceholder: (m) => `Minimum €${m}`,
    hintMin: (p, m, recurring) => `Minimum for ${p} is €${m}${recurring ? " per month" : ""}.`,
    opening: "Opening secure checkout…",
    subscribe: "Subscribe with Stripe",
    contribute: "Contribute with Stripe",
    payNote: "Payments are processed securely by Stripe. You will be redirected to a protected checkout page.",
    errCheckoutOpen: "Could not open the secure checkout. Please try again.",
    errCheckoutStart: "Could not start the secure checkout. Please try again.",
  },
  de: {
    reqTitle: "Teile dein Anliegen",
    reqSub: "Jedes Feld wird vertraulich und mit Wärme gehalten.",
    nameLabel: "Dein Name *",
    namePlaceholder: "Dein vollständiger Name",
    emailLabel: "Deine E-Mail *",
    messageLabel: "Nachricht / Absicht *",
    messagePlaceholder: "Teile, was auch immer du gerade trägst — so knapp oder so ausführlich, wie es sich stimmig anfühlt.",
    pathLabel: "Weg *",
    pathPlaceholder: "Wähle einen Weg",
    messageOnly: "Nur Nachricht senden",
    continueDonation: "Weiter zur Spende",
    needName: "Bitte teile deinen Namen mit.",
    needEmail: "Bitte gib eine gültige E-Mail-Adresse ein.",
    needMessage: "Bitte teile ein paar Worte zu deiner Absicht mit.",
    needPath: "Bitte wähle einen Weg.",
    invalidAmount: "Bitte gib einen gültigen Betrag ein.",
    minMsg: (p, m, unit) => `Der Mindestbeitrag für ${p} beträgt ${m} €${unit}.`,
    perMonth: " pro Monat",
    sentTitle: "Dein Anliegen ist eingegangen",
    sentBody: (name, id) => (
      <>Danke, {name}. Deine Absicht ruht bei uns und wir werden persönlich antworten. Deine Referenz lautet <strong style={{ color: "var(--ink)" }}>{id}</strong>.</>
    ),
    sendAnother: "Weiteres Anliegen senden",
    okMessage: "Deine Nachricht ist eingegangen. Wir melden uns achtsam bei dir.",
    errSend: "Etwas hat das Senden unterbrochen. Bitte versuche es erneut.",
    sundayNote: "Jeden Sonntag · 09:00 Uhr (CEST) · Etwa 20 Minuten · Kostenfrei",
    sundayRegister: "Für die Sonntagssitzung anmelden",
    sundaySentTitle: "Du bist angemeldet",
    sundaySentBody: "Danke. Du bist für die Sonntags-Gemeinschaftssitzung angemeldet. Du erhältst die Details per E-Mail.",
    sundayDefaultMsg: "Ich möchte an der kostenlosen Sonntags-Gemeinschaftssitzung teilnehmen.",
    back: "Zurück zum Anliegen",
    danaTitle: "Biete dein Dana dar",
    danaSub: (p, recurring, min, max) => (
      <>Für <strong style={{ color: "var(--ink)" }}>{p}</strong> — ein {recurring ? "monatlicher Beitrag" : "einmaliger Beitrag"} in Euro. Vorgeschlagener Bereich {min} €–{max} €{recurring ? " / Monat" : ""}.</>
    ),
    monthlyLabel: "Monatlicher Beitrag (EUR)",
    onceLabel: "Beitrag (EUR)",
    minPlaceholder: (m) => `Mindestens ${m} €`,
    hintMin: (p, m, recurring) => `Mindestbeitrag für ${p} beträgt ${m} €${recurring ? " pro Monat" : ""}.`,
    opening: "Sicherer Bezahlvorgang wird geöffnet…",
    subscribe: "Mit Stripe abonnieren",
    contribute: "Mit Stripe beitragen",
    payNote: "Zahlungen werden sicher von Stripe verarbeitet. Du wirst zu einer geschützten Bezahlseite weitergeleitet.",
    errCheckoutOpen: "Der sichere Bezahlvorgang konnte nicht geöffnet werden. Bitte versuche es erneut.",
    errCheckoutStart: "Der sichere Bezahlvorgang konnte nicht gestartet werden. Bitte versuche es erneut.",
  },
};

const initial = {
  requesterName: "",
  requesterEmail: "",
  message: "",
  path: "",
};

const isEmail = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);

const RequestDonationFlow = () => {
  const location = useLocation();
  const lang = getLang(location.pathname);
  const t = T[lang];
  const labelFor = (p) => (p === SUNDAY_PATH ? SUNDAY_LABELS[lang] : (PATH_LABELS[lang][p] || p));

  const [step, setStep] = useState("request"); // request | donation | sent
  const [form, setForm] = useState(initial);
  const [busy, setBusy] = useState(false);
  const [requestId, setRequestId] = useState("");
  const [amount, setAmount] = useState("");

  const cfg = form.path ? PATHS[form.path] : null;
  const isSunday = form.path === SUNDAY_PATH;

  const set = (key) => (e) =>
    setForm((f) => ({ ...f, [key]: e?.target ? e.target.value : e }));

  const validateRequest = (action) => {
    if (!form.requesterName.trim()) return t.needName;
    if (!isEmail(form.requesterEmail)) return t.needEmail;
    if (!isSunday && !form.message.trim()) return t.needMessage;
    if (action === "donation" && !form.path) return t.needPath;
    return null;
  };

  const submitRequest = async (action) => {
    const err = validateRequest(action);
    if (err) {
      toast.error(err);
      return;
    }
    setBusy(true);
    try {
      const payload = { ...form, action, lang };
      if (isSunday && !form.message.trim()) payload.message = t.sundayDefaultMsg;
      const { data } = await axios.post(`${API}/requests`, payload);
      setRequestId(data.request_id);
      if (action === "message_only") {
        setStep("sent");
        toast.success(isSunday ? t.sundaySentBody : t.okMessage);
      } else {
        setStep("donation");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || t.errSend);
    } finally {
      setBusy(false);
    }
  };

  const amountError = useMemo(() => {
    if (!cfg || amount === "") return null;
    const n = Number(amount);
    if (Number.isNaN(n) || n <= 0) return t.invalidAmount;
    if (n < cfg.min) {
      const unit = cfg.recurring ? t.perMonth : "";
      return t.minMsg(labelFor(form.path), cfg.min, unit);
    }
    return null;
  }, [amount, cfg, form.path, lang]);

  const canPay = cfg && amount !== "" && !amountError && !busy;

  const startDonation = async () => {
    if (!canPay) return;
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/payments/checkout`, {
        request_id: requestId,
        amount: Number(amount),
        origin_url: window.location.origin,
        lang,
      });
      if (data.checkout_url) {
        window.location.href = data.checkout_url;
      } else {
        toast.error(t.errCheckoutOpen);
        setBusy(false);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || t.errCheckoutStart);
      setBusy(false);
    }
  };

  /* ---------- Confirmation (message only) ---------- */
  if (step === "sent") {
    return (
      <div className="soft-card unified-card unified-sent">
        <div className="unified-sent-icon"><Check size={26} /></div>
        <h2 className="unified-title" style={{ marginTop: "1rem" }}>{isSunday ? t.sundaySentTitle : t.sentTitle}</h2>
        <p className="unified-sub" style={{ maxWidth: "44ch", margin: "0.75rem auto 0" }}>
          {isSunday ? t.sundaySentBody : t.sentBody(form.requesterName.split(" ")[0], requestId)}
        </p>
        <button
          className="btn-ghost"
          style={{ marginTop: "1.75rem" }}
          onClick={() => { setForm(initial); setAmount(""); setRequestId(""); setStep("request"); }}
        >
          {t.sendAnother}
        </button>
      </div>
    );
  }

  /* ---------- Donation step ---------- */
  if (step === "donation") {
    return (
      <div className="soft-card unified-card">
        <button className="unified-back" onClick={() => setStep("request")} type="button">
          <ArrowLeft size={15} /> {t.back}
        </button>
        <div className="unified-head">
          <span className="unified-mark"><Heart size={17} color="var(--gold-ink)" /></span>
          <div>
            <h2 className="unified-title">{t.danaTitle}</h2>
            <p className="unified-sub">
              {t.danaSub(labelFor(form.path), cfg.recurring, cfg.min, cfg.max)}
            </p>
          </div>
        </div>

        <label className="field-label">
          {cfg.recurring ? t.monthlyLabel : t.onceLabel}
        </label>
        <div className="amount-field">
          <span className="amount-symbol">€</span>
          <Input
            className="amount-input"
            type="number"
            inputMode="decimal"
            min={cfg.min}
            step="1"
            placeholder={t.minPlaceholder(cfg.min)}
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
          />
        </div>
        <p className={`amount-hint${amountError ? " is-error" : ""}`}>
          {amountError || t.hintMin(labelFor(form.path), cfg.min, cfg.recurring)}
        </p>

        <button className="btn-gold w-full dana-pay-btn" onClick={startDonation} disabled={!canPay}>
          {busy ? (<><Loader2 size={16} className="spin" /> {t.opening}</>)
                : (<><Heart size={16} /> {cfg.recurring ? t.subscribe : t.contribute}</>)}
        </button>
        <p className="dana-pay-note">
          <ShieldCheck size={13} style={{ verticalAlign: "-2px", marginRight: 4 }} />
          {t.payNote}
        </p>
      </div>
    );
  }

  /* ---------- Request step (default, always first) ---------- */
  return (
    <div className="soft-card unified-card">
      <div className="unified-head">
        <span className="unified-mark"><Send size={16} color="var(--gold-ink)" /></span>
        <div>
          <h2 className="unified-title">{t.reqTitle}</h2>
          <p className="unified-sub">{t.reqSub}</p>
        </div>
      </div>

      <div className="unified-row2">
        <div>
          <label className="field-label">{t.nameLabel}</label>
          <Input value={form.requesterName} onChange={set("requesterName")} placeholder={t.namePlaceholder} />
        </div>
        <div>
          <label className="field-label">{t.emailLabel}</label>
          <Input type="email" value={form.requesterEmail} onChange={set("requesterEmail")} placeholder="you@example.com" />
        </div>
      </div>

      <div style={{ marginTop: "1rem" }}>
        <label className="field-label">{t.messageLabel}</label>
        <Textarea
          rows={5}
          value={form.message}
          onChange={set("message")}
          placeholder={t.messagePlaceholder}
        />
      </div>

      <div style={{ marginTop: "1rem" }}>
        <label className="field-label">{t.pathLabel}</label>
        <Select value={form.path} onValueChange={set("path")}>
          <SelectTrigger>
            <SelectValue placeholder={t.pathPlaceholder} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={SUNDAY_PATH}>{SUNDAY_LABELS[lang]}</SelectItem>
            {PATH_NAMES.map((p) => (
              <SelectItem key={p} value={p}>{labelFor(p)}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        {isSunday && (
          <p className="amount-hint" style={{ marginTop: "0.6rem" }}>{t.sundayNote}</p>
        )}
      </div>

      <div className="unified-actions">
        {isSunday ? (
          <button type="button" className="btn-gold unified-btn" onClick={() => submitRequest("message_only")} disabled={busy}>
            {busy ? <Loader2 size={16} className="spin" /> : <Heart size={16} />} {t.sundayRegister}
          </button>
        ) : (
          <>
            {!form.path && (
              <button type="button" className="btn-ghost unified-btn" onClick={() => submitRequest("message_only")} disabled={busy}>
                {busy ? <Loader2 size={16} className="spin" /> : <Send size={16} />} {t.messageOnly}
              </button>
            )}
            <button type="button" className="btn-gold unified-btn" onClick={() => submitRequest("donation")} disabled={busy}>
              <Heart size={16} /> {t.continueDonation}
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default RequestDonationFlow;
